/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import {
  acceptInvitation,
  checkAiAssistantAccess,
  checkReportDownloadAccess,
  checkSmrtExplorerAccess,
  fetchSubscriptionStatus,
  getFormationInfo,
  loginUser,
  regUser,
} from './userService';

const initialState = {
  aiAssistantEnabled: null,
  canDownloadReport: null,
  errorLogin: '',
  errorRegistration: '',
  loading: false,
  smrtExplorerAllowed: null,
  token: '',
  userProfile: {},
  userType: null,
  userRoles: [],
  mappedPlayerId: null,
  // Admin "view-as" (C1): the player a platform admin chose to inspect in the
  // PWA. null for real players / admins who haven't picked one yet.
  viewAsPlayerId: null,
  organizationId: null,
  // 'agency' | 'club' | null — which vocabulary/base path the org screens use.
  organizationKind: null,
  subscription: null,
};

const deriveRoles = (payload) => {
  if (Array.isArray(payload?.roles) && payload.roles.length > 0) {
    return payload.roles.filter(Boolean);
  }
  if (payload?.user_type) {
    return [payload.user_type];
  }
  return [];
};

const applyAuthContext = (state, payload) => {
  state.userType = payload?.user_type ?? null;
  state.userRoles = deriveRoles(payload);
  state.mappedPlayerId = payload?.mapped_player_id ?? null;
  state.viewAsPlayerId = payload?.view_as_player_id ?? null;
  state.organizationId = payload?.organization_id ?? null;
  state.organizationKind = payload?.organization_kind ?? null;
  state.subscription = payload?.subscription ?? null;
};

export const userSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.errorLogin = '';
        state.loading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload?.token ?? '';
        applyAuthContext(state, action.payload);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.errorLogin = action.payload;
      })
      .addCase(regUser.pending, (state) => {
        state.errorRegistration = '';
        state.loading = true;
      })
      .addCase(regUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        applyAuthContext(state, action.payload);
      })
      .addCase(regUser.rejected, (state, action) => {
        state.loading = false;
        state.errorRegistration = action.payload;
      })
      .addCase(acceptInvitation.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload?.token ?? '';
        applyAuthContext(state, action.payload);
      })
      .addCase(fetchSubscriptionStatus.fulfilled, (state, action) => {
        state.subscription = action.payload && action.payload.status ? action.payload : null;
      })
      .addCase(checkAiAssistantAccess.fulfilled, (state, action) => {
        state.aiAssistantEnabled = action.payload.allowed;
      })
      .addCase(checkAiAssistantAccess.rejected, (state) => {
        state.aiAssistantEnabled = false;
      })
      .addCase(checkSmrtExplorerAccess.fulfilled, (state, action) => {
        state.smrtExplorerAllowed = action.payload.allowed;
      })
      .addCase(checkSmrtExplorerAccess.rejected, (state) => {
        state.smrtExplorerAllowed = false;
      })
      .addCase(checkReportDownloadAccess.fulfilled, (state, action) => {
        state.canDownloadReport = action.payload.allowed;
      })
      .addCase(checkReportDownloadAccess.rejected, (state) => {
        state.canDownloadReport = false;
      })
      .addCase(getFormationInfo.pending, (state) => {
        state.userProfile = {};
        state.loading = true;
      })
      .addCase(getFormationInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.userProfile = action.payload;
      })
      .addCase(getFormationInfo.rejected, (state) => {
        state.loading = false;
      })
  },
  initialState,
  name: 'user',
  reducers: {
    changeOffsetBegin: (state, action) => {
      state.userProfile.offset_range_begin = Number(action.payload)
    },
    changeOffsetEnd: (state, action) => {
      state.userProfile.offset_range_end = Number(action.payload)
    },
    changeMergeOffset: (state, action) => {
      state.userProfile.merge_offset = Number(action.payload)
    },
    setPitchSettings: (state, action) => {
      state.userProfile.pitchSettings = action.payload
    },
    setPlaylistDragCopy: (state, action) => {
      state.userProfile.playlist_drag_copy = action.payload
    },
    setPlaylistRemoveDuplicates: (state, action) => {
      state.userProfile.playlist_remove_duplicates = action.payload
    },
    setPlaylistAllowMerge: (state, action) => {
      state.userProfile.playlist_allow_merge = action.payload
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    setAuthContext: (state, action) => {
      applyAuthContext(state, action.payload);
    },
    clearAuthContext: (state) => {
      state.userType = null;
      state.userRoles = [];
      state.mappedPlayerId = null;
      state.viewAsPlayerId = null;
      state.organizationId = null;
      state.organizationKind = null;
      state.subscription = null;
    },
    // Admin "view-as" (C1): bind the PWA session to a chosen player. We also
    // mirror it onto mappedPlayerId so every existing PWA hook (which reads
    // mappedPlayerId for the {id}-carrying endpoints) works unchanged.
    setViewAsPlayer: (state, action) => {
      const id = action.payload != null ? Number(action.payload) : null;
      state.viewAsPlayerId = id;
      state.mappedPlayerId = id;
    },
  },
});

export const { actions: userActions } = userSlice;
export const { reducer: userReducer } = userSlice;
